<template>
    <app-layout title="Settings">
        <page-header>
            <template #title>Settings - Holidays</template>

            <template #actions>
                <jet-button type="button" class="h-7 leading-none" @click="showAddHolidayModal = true" >
                    Add Holiday
                </jet-button>
            </template>

        </page-header>

        <settings-navigation />

        <div class="px-8 mb-4 xl:flex">
            <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                <table class="table w-full">
                    <thead>
                        <tr>
                            <th class="text-left">Name</th>
                            <th class="text-left">Date</th>
                            <th class="text-left">Overrides</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="holiday in holidays" :key="holiday.id">
                            <td>{{ holiday.name }}</td>
                            <td>{{ holiday.date }}</td>
                            <td>
                                <span v-if="holiday.customer_overrides.length > 0">
                                    {{ holiday.customer_overrides.length }} Customer{{ holiday.customer_overrides.length != 1 ? 's' : '' }}
                                </span>
                            </td>
                            <td class="text-right">
                                <jet-secondary-button class="mx-4" @click="editHoliday(holiday)">
                                    Edit
                                </jet-secondary-button>

                                <jet-danger-button class="mx-4" @click="deleteHoliday(holiday)">
                                    Delete
                                </jet-danger-button>
                            </td>
                        </tr>
                        <tr v-if="!holidays.length">
                            <td colspan="3" class="text-center py-8">No Holidays Defined</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Manage Override Form -->
            <jet-dialog-modal :show="showManageOverrideModal" @close="cancelAddHoliday">
                <template #title>
                     <h3 class="text-2xl">Override for {{ this.selectedHoliday.name }}</h3>
                </template>

                <template #content>

                </template>

                <template #footer>
                    <jet-tertiary-button @click="cancelManageOverride">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>


            <!-- Add Holiday -->
            <jet-dialog-modal :show="showAddHolidayModal" @close="cancelAddHoliday">
                <template #title>
                     <h3 class="text-2xl">Add Holiday</h3>
                </template>

                <template #content>
                    <form @submit.prevent="addHoliday" class="my-6">
                        <div class="form-group">
                            <jet-label value="Name" class="mb-0" />
                            <jet-input v-model="form.name" type="text"  />
                            <jet-input-error :message="form.errors.name" />
                        </div>

                        <div class="form-group">
                            <jet-label value="Date" class="mb-0" />
                            <jet-input v-model="form.date" type="date"  />
                            <jet-input-error :message="form.errors.date" />
                        </div>

                        <div class="form-group">
                            <table class="table striped w-full">
                                <thead>
                                    <tr>
                                        <th class="text-left">Customer Overrides</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="customer_override, index in this.form.customer_overrides" :key="index">
                                        <td>{{ customer_override.customer.name }}</td>
                                        
                                        <td class="text-center" style="width: 56px;">
                                            <jet-secondary-button type="button" @click="removeOverride(index)">
                                                <img src="/images/icons/trash_w.svg" width="16" height="16" alt="Delete" style="min-width: 16px;"/>
                                            </jet-secondary-button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="flex items-center space-x-2 my-3">
                                <multiselect
                                    class="multi-sm h-8 text-base"
                                    v-model="overrideCustomerId"
                                    :options="displayedCustomers"
                                    valueProp="id"
                                    trackBy="name"
                                    label="name"
                                    :searchable="true"
                                    placeholder="Select Customer"
                            />

                                <jet-button type="button" class="mr-2" @click="addOverride" :disabled="!overrideCustomerId">
                                    Add
                                </jet-button>
                            </div>
                        </div>

                        <input type="submit" class="hidden" ref="submitAddHoliday" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitAddHoliday.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="cancelAddHoliday">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Edit Holiday -->
            <jet-dialog-modal :show="showEditHolidayModal" @close="cancelEditHoliday">
                <template #title>
                     <h3 class="text-2xl">Edit Holiday</h3>
                </template>

                <template #content>
                    <form @submit.prevent="handleEditHoliday" class="my-6">
                        <div class="form-group">
                            <jet-label value="Name" class="mb-0" />
                            <jet-input v-model="form.name" type="text"  />
                            <jet-input-error :message="form.errors.name" />
                        </div>

                        <div class="form-group">
                            <jet-label value="Date" class="mb-0" />
                            <jet-input v-model="form.date" type="date"  />
                            <jet-input-error :message="form.errors.date" />
                        </div>

                        <div class="form-group">
                            <table class="table striped w-full">
                                <thead>
                                    <tr>
                                        <th class="text-left">Customer Overrides</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="customer_override, index in this.form.customer_overrides" :key="index">
                                        <td>{{ customer_override.customer.name }}</td>
                                        
                                        <td class="text-center" style="width: 56px;">
                                            <jet-secondary-button type="button" @click="removeOverride(index)">
                                                <img src="/images/icons/trash_w.svg" width="16" height="16" alt="Delete" style="min-width: 16px;"/>
                                            </jet-secondary-button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="flex items-center space-x-2 my-3">
                                <multiselect
                                    class="multi-sm h-8 text-base"
                                    v-model="overrideCustomerId"
                                    :options="displayedCustomers"
                                    valueProp="id"
                                    trackBy="name"
                                    label="name"
                                    :searchable="true"
                                    placeholder="Select Customer"
                            />

                                <jet-button type="button" class="mr-2" @click="addOverride" :disabled="!overrideCustomerId">
                                    Add
                                </jet-button>
                            </div>
                        </div>

                        <input type="submit" class="hidden" ref="submitEditHoliday" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitEditHoliday.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="cancelEditHoliday">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Holiday -->
            <jet-confirmation-modal :show="showDeleteHolidayModal" @close="cancelDeleteHoliday">
                <template #title>
                    Delete Holiday
                </template>

                <template #content>
                    Are you sure you would like to delete <strong>{{ form.name }}</strong>?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteHoliday">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="cancelDeleteHoliday">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>


        </div>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetInputError from '@/Jetstream/InputError';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';


export default {
    setup() {

        const form = useForm({
            id: null,
            name: null,
            date: null,
            customer_overrides: [],
        });

        return { form }

    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetInputError,
        JetLabel,
        JetDangerButton,
        JetDialogModal,
        JetConfirmationModal,
        JetSecondaryButton,

        JetTertiaryButton,
        SettingsNavigation,
        Multiselect
    },

    props: ['holidays', 'customers'],

    data() {
        return {
            showAddHolidayModal: false,
            showEditHolidayModal: false,
            showDeleteHolidayModal: false,
            showManageOverrideModal: false,
            selectedHoliday: null,

            overrideCustomerId: null,
        }
    },

    methods: {
        cancelAddHoliday() {
            this.form.reset();
            this.form.clearErrors();
            this.overrideCustomerId = null;
            this.showAddHolidayModal = false;
        },

        addHoliday() {
            this.form.post(this.route('settings.holidays.store'), {
                onSuccess: () => {
                    this.form.reset();
                    this.overrideCustomerId = null;
                    this.showAddHolidayModal = false;
                }
            });
        },

        editHoliday(holiday) {
            this.form.id = holiday.id;
            this.form.name = holiday.name;
            this.form.date = holiday.date;
            this.form.customer_overrides = JSON.parse(JSON.stringify(holiday.customer_overrides));
            this.overrideCustomerId = null;
            this.showEditHolidayModal = true;
        },

        cancelEditHoliday(holiday) {
            this.form.reset();
            this.form.clearErrors();
            this.overrideCustomerId = null;
            this.showEditHolidayModal = false;
        },

        handleEditHoliday() {
            this.form.put(this.route('settings.holidays.update', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.form.clearErrors();
                    this.overrideCustomerId = null;
                    this.showEditHolidayModal = false;
                }
            });
        },

        deleteHoliday(holiday) {
            this.form.id = holiday.id;
            this.form.name = holiday.name;
            this.showDeleteHolidayModal = true;
        },
        cancelDeleteHoliday() {
            this.form.reset();
            this.showDeleteHolidayModal = false;
        },
        handleDeleteHoliday() {
            this.form.delete(this.route('settings.holidays.destroy', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.showDeleteHolidayModal = false;
                }
            });
        },

        removeOverride(index){
            this.form.customer_overrides.splice(index, 1);
        },

        addOverride() {
            this.form.customer_overrides.push({
                id: null,
                holiday_id: this.form.id,
                customer_id: this.overrideCustomerId,
                customer: this.customers.find((customer) => customer.id == this.overrideCustomerId),
            });
            this.overrideCustomerId = null;
        }
    },

    computed: {
        displayedCustomers() {
            return this.customers.filter(c => !this.form.customer_overrides.some(co => co.customer_id == c.id));
        }
    },

    watch: {

    },

    mounted() {
    }
}
</script>

<style scoped>


</style>
